import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { InfoBox } from '@components/info-box';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';

import { getErrorMessage } from './utils';

export const H3CoreSettingsPage = observer(() => {
  const [state, setState] = useState({
    baseUrl: '',
    companyName: '',
    username: '',
    password: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const settingsQuery = useQuery({
    queryKey: ['h3coreSettings'],
    queryFn: api.integrations.h3coreGetSettings,
    onSuccess: (data) => {
      setState((old) => ({
        ...old,
        baseUrl: data.baseUrl,
        companyName: data.companyName,
        username: data.username,
      }));
    },
  });
  function saveSettings() {
    setSubmitting(true);
    api.integrations
      .h3coreSaveSettings(state.baseUrl, state.companyName, state.username, state.password)
      .then(() => {
        showSuccess(t('integrations.h3core.settings.save.success'));
        setErrorMessage('');
      })
      .catch((e) => {
        setErrorMessage(getErrorMessage(e.response.data.reason, t));
      })
      .finally(() => {
        setSubmitting(false);
      });
  }
  return (
    <Stack space={4}>
      <Text font="heading2" text="H3Core" />
      <Separator />
      {(settingsQuery.isLoading || submitting) && <Loading fixed />}
      {settingsQuery.isSuccess && (
        <Stack space={6}>
          <Text text={t('integrations.h3core.settings.description')} />
          {errorMessage && <InfoBox variant="red" text={errorMessage} />}
          <TextField
            label={t('integrations.h3core.settings.url.label')}
            isDisabled={submitting}
            onChange={(value) => setState((old) => ({ ...old, baseUrl: value }))}
            defaultValue={state.baseUrl}
          />
          <TextField
            label={t('integrations.h3core.settings.companyname.label')}
            isDisabled={submitting}
            onChange={(value) => setState((old) => ({ ...old, companyName: value }))}
            defaultValue={state.companyName}
          />
          <TextField
            label={t('integrations.h3core.settings.username.label')}
            isDisabled={submitting}
            onChange={(value) => setState((old) => ({ ...old, username: value }))}
            defaultValue={state.username}
          />
          <TextField
            label={t('integrations.h3core.settings.password.label')}
            isDisabled={submitting}
            onChange={(value) => setState((old) => ({ ...old, password: value }))}
          />
          <Button
            text={t('integrations.hireflix.settings.submit.label')}
            isDisabled={Object.values(state).some((value) => value === '') || submitting}
            onClick={saveSettings}
          />
        </Stack>
      )}
    </Stack>
  );
});
